import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  fetchOrganisationDesignationDefaults,
  fetchVariantDesignationOverrides,
} from '@/hooks/useVariantMappingRows';
import { boughtAsOptions, canOverrideBoughtAs, canOverrideSoldAs, soldAsOptions } from '@/lib/designations/rowOverrides';
import {
  APPLY_FAILURE_MESSAGE,
  applyColumnRemoval,
  defaultsWithLeftmostColumn,
  designationForNewRow,
  effectiveCell,
  leftmostReplacement,
  planApply,
  rowNeedsDesignationWarning,
  type ApplyPolicy,
  type ConflictCopy,
  type DesignationDefaults,
  type DesignationVariantRow,
  type IdentifierRef,
} from '@/lib/designations/organisationDefaults';
import {
  applyOrganisationDefaults,
  defaultsFromRow,
  reassignRemovedDefaultColumn,
} from '@/lib/designations/persistDesignationDefaults';
import { variantManagementCellValue } from '@/shared/variantMapping/variantManagementColumns';
import type { VariantManagementColumn, VariantManagementRow } from '@/shared/types/variantMapping';

const EMPTY_DEFAULTS: DesignationDefaults = { soldAs: null, boughtAs: null };

function refFromColumn(column: VariantManagementColumn): IdentifierRef {
  const separator = column.key.indexOf(':');
  const identifierType = separator === -1 ? column.key : column.key.slice(0, separator);
  return { columnKey: column.key, identifierType };
}

export function useOrganisationDefaultColumns(input: {
  organisationId: string;
  rows: VariantManagementRow[];
  columns: VariantManagementColumn[];
  stableColumns: VariantManagementColumn[];
  isAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [soldKey, setSoldKey] = useState<string | null>(null);
  const [boughtKey, setBoughtKey] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ConflictCopy | null>(null);
  const [applying, setApplying] = useState(false);
  const reassigning = useRef<string | null>(null);

  const defaultsQuery = useQuery({
    queryKey: ['organisation-designation-defaults', input.organisationId],
    queryFn: async () => defaultsFromRow(await fetchOrganisationDesignationDefaults(input.organisationId)),
  });
  const overridesQuery = useQuery({
    queryKey: ['variant-designation-overrides', input.organisationId],
    queryFn: () => fetchVariantDesignationOverrides(input.organisationId),
  });

  const saved = defaultsQuery.data ?? EMPTY_DEFAULTS;
  const effectiveDefaults = useMemo(() => {
    if (!defaultsQuery.isFetched) return saved;
    const columns = input.stableColumns.length > 0 ? input.stableColumns : input.columns;
    return defaultsWithLeftmostColumn(saved, columns.map((column) => column.key));
  }, [defaultsQuery.isFetched, input.columns, input.stableColumns, saved]);

  const designationRows = useMemo<DesignationVariantRow[]>(() => {
    const overrides = new Map((overridesQuery.data ?? []).map((row) => [row.master_product_id, row]));
    return input.rows.map((row) => {
      const override = overrides.get(row.variantId);
      const valuesByColumn: Record<string, string> = {};
      const valueColumns = input.stableColumns.length > 0 ? input.stableColumns : input.columns;
      for (const column of valueColumns) {
        valuesByColumn[column.key] = variantManagementCellValue(row, column);
      }
      return {
        masterProductId: row.variantId,
        eveId: row.eveId,
        soldAsOverride:
          override?.sold_as_column_key && override.sold_as_identifier_type
            ? { columnKey: override.sold_as_column_key, identifierType: override.sold_as_identifier_type }
            : null,
        boughtAsOverride:
          override?.bought_as_column_key && override.bought_as_identifier_type
            ? { columnKey: override.bought_as_column_key, identifierType: override.bought_as_identifier_type }
            : null,
        valuesByColumn,
      };
    });
  }, [input.columns, input.rows, input.stableColumns, overridesQuery.data]);

  const soldAsByRow = useMemo(() => {
    const presentation: Record<string, { columnKey: string | null; missing: boolean; canOverride: boolean; hasOverride: boolean }> = {};
    for (const row of designationRows) {
      const cell = effectiveCell(row, 'sold_as', effectiveDefaults);
      const values = row.valuesByColumn;
      const options = soldAsOptions({
        columns: input.columns,
        valuesByColumn: values,
        effectiveColumnKey: cell.columnKey,
        effectiveMissing: cell.missing,
      });
      presentation[row.masterProductId] = {
        columnKey: cell.columnKey,
        missing: cell.missing,
        canOverride: canOverrideSoldAs(options),
        hasOverride: row.soldAsOverride != null,
      };
    }
    return presentation;
  }, [designationRows, effectiveDefaults, input.columns]);

  const boughtAsByRow = useMemo(() => {
    const presentation: Record<string, { columnKey: string | null; missing: boolean; canOverride: boolean; hasOverride: boolean }> = {};
    for (const row of designationRows) {
      const cell = effectiveCell(row, 'bought_as', effectiveDefaults);
      const options = boughtAsOptions({
        columns: input.columns,
        valuesByColumn: row.valuesByColumn,
        effectiveColumnKey: cell.columnKey,
        effectiveMissing: cell.missing,
      });
      presentation[row.masterProductId] = {
        columnKey: cell.columnKey,
        missing: cell.missing,
        canOverride: canOverrideBoughtAs(options),
        hasOverride: row.boughtAsOverride != null,
      };
    }
    return presentation;
  }, [designationRows, effectiveDefaults, input.columns]);

  const warningByRow = useMemo(() => {
    const flags = new Map<string, boolean>();
    for (const row of designationRows) flags.set(row.masterProductId, rowNeedsDesignationWarning(row, effectiveDefaults));
    return flags;
  }, [designationRows, effectiveDefaults]);

  useEffect(() => {
    if (editing || !saved.soldAs && !saved.boughtAs) return;
    const removed = [saved.soldAs, saved.boughtAs].filter(
      (ref): ref is IdentifierRef =>
        ref != null && !input.stableColumns.some((column) => column.key === ref.columnKey),
    );
    if (removed.length === 0 || input.stableColumns.length === 0) {
      reassigning.current = null;
      return;
    }
    if (reassigning.current === removed[0].columnKey) return;
    const replacementColumn = leftmostReplacement(
      input.stableColumns.map((column) => ({ key: column.key, identifierType: refFromColumn(column).identifierType })),
      removed[0].columnKey,
    );
    if (!replacementColumn) return;
    const replacement = input.stableColumns.find((column) => column.key === replacementColumn.key);
    if (!replacement) return;
    const removedKey = removed[0].columnKey;
    reassigning.current = removedKey;
    void reassignRemovedDefaultColumn(supabase, {
      organisationId: input.organisationId,
      removedColumnKey: removedKey,
      replacement: refFromColumn(replacement),
    })
      .then((result) => {
        if (!result.ok) {
          if (reassigning.current === removedKey) reassigning.current = null;
          toast.error(result.message || APPLY_FAILURE_MESSAGE);
          return;
        }
        if (result.reassigned) {
          queryClient.invalidateQueries({ queryKey: ['organisation-designation-defaults', input.organisationId] });
          queryClient.invalidateQueries({ queryKey: ['variant-designation-overrides', input.organisationId] });
        }
      })
      .catch(() => {
        if (reassigning.current === removedKey) reassigning.current = null;
      });
  }, [editing, input.organisationId, input.stableColumns, queryClient, saved.boughtAs, saved.soldAs]);

  const beginEditing = useCallback(() => {
    if (!input.isAdmin) {
      toast.error('This action requires the admin role.');
      return;
    }
    if (input.rows.length === 0) return;
    setSoldKey(effectiveDefaults.soldAs?.columnKey ?? input.columns[0]?.key ?? null);
    setBoughtKey(effectiveDefaults.boughtAs?.columnKey ?? input.columns[0]?.key ?? null);
    setConflict(null);
    setEditing(true);
  }, [effectiveDefaults.boughtAs, effectiveDefaults.soldAs, input.columns, input.isAdmin, input.rows.length]);

  const cancelEditing = useCallback(() => {
    setEditing(false);
    setConflict(null);
    setSoldKey(effectiveDefaults.soldAs?.columnKey ?? null);
    setBoughtKey(effectiveDefaults.boughtAs?.columnKey ?? null);
  }, [effectiveDefaults.boughtAs, effectiveDefaults.soldAs]);

  const selectionDefaults = useCallback((): DesignationDefaults => {
    const sold = input.columns.find((column) => column.key === soldKey);
    const bought = input.columns.find((column) => column.key === boughtKey);
    return {
      soldAs: sold ? refFromColumn(sold) : effectiveDefaults.soldAs,
      boughtAs: bought ? refFromColumn(bought) : effectiveDefaults.boughtAs,
    };
  }, [boughtKey, effectiveDefaults.boughtAs, effectiveDefaults.soldAs, input.columns, soldKey]);

  const finishApply = useCallback(
    async (policy: ApplyPolicy | null) => {
      setApplying(true);
      try {
        const next = selectionDefaults();
        const result = await applyOrganisationDefaults(supabase, {
          organisationId: input.organisationId,
          soldAs: next.soldAs,
          boughtAs: next.boughtAs,
          policy,
        });
        if (!result.ok) {
          toast.error(result.message || APPLY_FAILURE_MESSAGE);
          return;
        }
        if (result.conflict && result.role_label && typeof result.count === 'number') {
          const plan = planApply({ rows: designationRows, current: saved, next });
          setConflict(plan.ok && plan.conflict ? plan.copy : null);
          return;
        }
        setEditing(false);
        setConflict(null);
        await queryClient.invalidateQueries({ queryKey: ['organisation-designation-defaults', input.organisationId] });
        await queryClient.invalidateQueries({ queryKey: ['variant-designation-overrides', input.organisationId] });
      } finally {
        setApplying(false);
      }
    },
    [designationRows, input.organisationId, queryClient, saved, selectionDefaults],
  );

  const requestApply = useCallback(() => {
    const next = selectionDefaults();
    const plan = planApply({ rows: designationRows, current: saved, next });
    if (!plan.ok) {
      toast.error(APPLY_FAILURE_MESSAGE);
      return;
    }
    if (plan.conflict) {
      setConflict(plan.copy);
      return;
    }
    const changed = plan.defaults.soldAs !== saved.soldAs || plan.defaults.boughtAs !== saved.boughtAs;
    void finishApply(changed ? 'follow_defaults' : null);
  }, [designationRows, finishApply, saved, selectionDefaults]);

  const confirmPolicy = useCallback(
    (policy: ApplyPolicy) => {
      setConflict(null);
      void finishApply(policy);
    },
    [finishApply],
  );

  const queriesIdle =
    defaultsQuery.isFetched &&
    overridesQuery.isFetched &&
    !defaultsQuery.isFetching &&
    !overridesQuery.isFetching;
  return {
    designationsReady: queriesIdle,
    editing,
    applying,
    conflict,
    soldAsColumnKey: editing ? soldKey : effectiveDefaults.soldAs?.columnKey ?? null,
    boughtAsColumnKey: editing ? boughtKey : effectiveDefaults.boughtAs?.columnKey ?? null,
    savedSoldAsColumnKey: effectiveDefaults.soldAs?.columnKey ?? null,
    savedBoughtAsColumnKey: effectiveDefaults.boughtAs?.columnKey ?? null,
    warningByRow,
    soldAsByRow,
    boughtAsByRow,
    beginEditing,
    cancelEditing,
    setSoldKey,
    setBoughtKey,
    requestApply,
    confirmPolicy,
    clearConflict: () => setConflict(null),
    previewForNewRow: (valuesByColumn: Record<string, string | null | undefined>) =>
      designationForNewRow(effectiveDefaults, valuesByColumn),
    removalPlan: applyColumnRemoval,
  };
}
